import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';

// Auth
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';

// User
import UserDashboard from './pages/user/Dashboard';
import SetupFee from './pages/user/SetupFee';
import WalletPage from './pages/user/Wallet';
import WalletRechargePage from './pages/user/WalletRecharge';
import MT5Page from './pages/user/MT5';
import ReferralPage from './pages/user/Referral';
import CommissionPage from './pages/user/Commission';
import WithdrawalPage from './pages/user/Withdrawal';
import ProfitHistoryPage from './pages/user/ProfitHistory';
import ProfilePage from './pages/user/Profile';
import AchievementsPage from './pages/user/Achievements';
import InvestPage from './pages/user/Invest';
import SupportTicketsPage from './pages/user/SupportTickets';

// Admin
import AdminDashboard from './pages/admin/Dashboard';
import AdminUsers from './pages/admin/Users';
import AdminTicketsPage from './pages/admin/Tickets';
import AdminUserDetail from './pages/admin/UserDetail';
import AdminProfits from './pages/admin/Profits';
import AdminWithdrawals from './pages/admin/Withdrawals';
import AdminTransactions from './pages/admin/Transactions';
import RankConfigPage from './pages/admin/RankConfig';
import PaymentConfigPage from './pages/admin/PaymentConfig';
import OfferConfigPage from './pages/admin/OfferConfig';
import WalletRequestsPage from './pages/admin/WalletRequests';
import BusinessDashboard from './pages/admin/BusinessDashboard';
import InvestorPackages from './pages/admin/InvestorPackages';
import FundManagement from './pages/admin/FundManagement';

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen bg-dark-900 flex items-center justify-center">
      <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
    </div>
  );
  if (!user) return <Navigate to="/login" />;
  if (adminOnly && user.role !== 'admin') return <Navigate to="/dashboard" />;
  return children;
};

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/" element={user ? <Navigate to={user.role === 'admin' ? '/admin' : '/dashboard'} /> : <Navigate to="/login" />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/register/:referralCode" element={<Register />} />

      {/* User Routes */}
      <Route path="/dashboard"      element={<ProtectedRoute><UserDashboard /></ProtectedRoute>} />
      <Route path="/setup-fee"      element={<ProtectedRoute><SetupFee /></ProtectedRoute>} />
      <Route path="/wallet"         element={<ProtectedRoute><WalletPage /></ProtectedRoute>} />
      <Route path="/wallet-recharge"element={<ProtectedRoute><WalletRechargePage /></ProtectedRoute>} />
      <Route path="/mt5"            element={<ProtectedRoute><MT5Page /></ProtectedRoute>} />
      <Route path="/referral"       element={<ProtectedRoute><ReferralPage /></ProtectedRoute>} />
      <Route path="/commission"     element={<ProtectedRoute><CommissionPage /></ProtectedRoute>} />
      <Route path="/withdrawal"     element={<ProtectedRoute><WithdrawalPage /></ProtectedRoute>} />
      <Route path="/profit-history" element={<ProtectedRoute><ProfitHistoryPage /></ProtectedRoute>} />
      <Route path="/profile"        element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/achievements"   element={<ProtectedRoute><AchievementsPage /></ProtectedRoute>} />
      <Route path="/invest"         element={<ProtectedRoute><InvestPage /></ProtectedRoute>} />
      <Route path="/support-tickets" element={<ProtectedRoute><SupportTicketsPage /></ProtectedRoute>} />

      {/* Admin Routes */}
      <Route path="/admin"                      element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/users"                element={<ProtectedRoute adminOnly><AdminUsers /></ProtectedRoute>} />
      <Route path="/admin/users/:id"            element={<ProtectedRoute adminOnly><AdminUserDetail /></ProtectedRoute>} />
      <Route path="/admin/profits"              element={<ProtectedRoute adminOnly><AdminProfits /></ProtectedRoute>} />
      <Route path="/admin/withdrawals"          element={<ProtectedRoute adminOnly><AdminWithdrawals /></ProtectedRoute>} />
      <Route path="/admin/transactions"         element={<ProtectedRoute adminOnly><AdminTransactions /></ProtectedRoute>} />
      <Route path="/admin/ranks"                element={<ProtectedRoute adminOnly><RankConfigPage /></ProtectedRoute>} />
      <Route path="/admin/payment-config"       element={<ProtectedRoute adminOnly><PaymentConfigPage /></ProtectedRoute>} />
      <Route path="/admin/offer"                element={<ProtectedRoute adminOnly><OfferConfigPage /></ProtectedRoute>} />
      <Route path="/admin/wallet-requests"      element={<ProtectedRoute adminOnly><WalletRequestsPage /></ProtectedRoute>} />
      <Route path="/admin/business"             element={<ProtectedRoute adminOnly><BusinessDashboard /></ProtectedRoute>} />
      <Route path="/admin/investor-packages"    element={<ProtectedRoute adminOnly><InvestorPackages /></ProtectedRoute>} />
      <Route path="/admin/fund"                 element={<ProtectedRoute adminOnly><FundManagement /></ProtectedRoute>} />
      <Route path="/admin/tickets"              element={<ProtectedRoute adminOnly><AdminTicketsPage /></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <Toaster position="top-right" toastOptions={{
          style: { background: '#1a2540', color: '#e2e8f0', border: '1px solid #22c55e33' }
        }} />
        <AppRoutes />
      </Router>
    </AuthProvider>
  );
}

export default App;
